import { useEffect } from 'react';
import { Tag } from 'antd';
import type { ColumnsType } from 'antd/lib/table';
import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';

import { dateFormatToLocale } from '../../../../helpers/dateTime.helper';
import { useAppDispatch, useAppSelector } from '../../../../hooks';
import { TableService } from '../../../../service/table.service';
import { compareDates } from '../program-dashboard.helper';
import type { Intervention } from '../programDashboardSlice';
import { InterventionType } from '../programDashboardSlice';
import { handleModalTableChange } from './modalDashboardSlice';

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

export const GetInterventionsModalProps = () => {
  const dispatch = useAppDispatch();
  const { getColumnSearchProps, clearSearch } = TableService();
  const sortedInfo = useAppSelector((state) => state.modalDashboardSlice.requestModalParam.sortedInfo);
  const filteredInfo = useAppSelector((state) => state.modalDashboardSlice.requestModalParam.filteredInfo);

  useEffect(() => {
    dispatch(
      handleModalTableChange({
        sorter: {
          columnKey: 'date',
          field: 'date',
          order: 'descend',
        },
      }),
    );
  }, [dispatch]);

  const columns: ColumnsType<Intervention> = [
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
      width: 150,
      sorter: (a, b) => compareDates(dayjs(a.date), dayjs(b.date)),
      ...getColumnSearchProps('created', filteredInfo.created),
      onFilter: (_value, record) => {
        const [startDate, endDate] = filteredInfo.created ?? [];
        if (startDate && !endDate) {
          dayjs().isSameOrAfter(dayjs('2011-01-01'));
          return dayjs(record.date).isSameOrAfter(dayjs(startDate));
        } else if (!startDate && endDate) {
          return dayjs(record.date).isSameOrBefore(dayjs(endDate));
        } else if (startDate && endDate) {
          return (
            dayjs(record.date).isSameOrAfter(dayjs(startDate)) && dayjs(record.date).isSameOrBefore(dayjs(endDate))
          );
        } else {
          return false;
        }
      },
      sortOrder: sortedInfo.field === 'date' ? sortedInfo.order : null,
      filteredValue: filteredInfo.created || null,
      render: (_text, record) => (record.date ? dateFormatToLocale(new Date(record.date)) : null),
    },
    {
      title: 'Client name',
      dataIndex: 'client_name',
      key: 'client_name',
      width: 175,
      sorter: (a, b) => a.client_name.localeCompare(b.client_name),
      onFilter: (value, record) => record.client_name.toLowerCase().includes((value as string).toLowerCase()),
      ...getColumnSearchProps('client_name', filteredInfo.client_name),
      sortOrder: sortedInfo.field === 'client_name' ? sortedInfo.order : null,
      filteredValue: filteredInfo.client_name || null,
      className: 'ph-no-capture',
    },
    {
      title: 'Sites',
      dataIndex: 'sites',
      key: 'sites',
      width: 100,
      ...getColumnSearchProps('sites', filteredInfo.sites),
      onFilter: (value, record) => record.sites?.some((s) => s.toLowerCase().includes((value as string).toLowerCase())),
      render: (_text, record) =>
        record.sites?.map((site, index) => {
          return (
            <div key={index}>
              {site !== null ? (
                <Tag color="geekblue" style={{ border: 'none' }}>
                  {site}
                </Tag>
              ) : null}
            </div>
          );
        }),
      sortOrder: sortedInfo.field === 'sites' ? sortedInfo.order : null,
      filteredValue: filteredInfo.sites || null,
    },
    {
      title: 'Clinician name',
      dataIndex: 'clinician_name',
      key: 'clinician_name',
      width: 175,
      sorter: (a, b) => a.clinician_name.localeCompare(b.clinician_name),
      onFilter: (value, record) => record.clinician_name.toLowerCase().includes((value as string).toLowerCase()),
      ...getColumnSearchProps('clinician_name', filteredInfo.clinician_name),
      sortOrder: sortedInfo.field === 'clinician_name' ? sortedInfo.order : null,
      filteredValue: filteredInfo.clinician_name || null,
    },
    {
      title: 'Severity',
      dataIndex: 'intervention_type',
      key: 'intervention_type',
      width: 100,
      filters: Object.values(InterventionType).map((value) => ({ text: value, value })),
      onFilter: (value, record) => record.intervention_type === value,
      filteredValue: filteredInfo.intervention_type || null,
    },
  ];

  return { columns, clearSearch };
};
